import React, { useState, useMemo, useCallback, useEffect } from 'react';
import jsPDF from 'jspdf';
import { formatMontant } from '../../utils/helpers';
import { computeLigne, RECETTES_LIGNES, DEPENSES_LIGNES, HIST_DEFAULT, HIST_YEARS } from './comptaUtils';


// ─── Lignes RECETTES ─────────────────────────────────────────────────────────

const fmt = (v) => {
  if (!v || v === 0) return <span style={{ color:'#9ca3af' }}>—</span>;
  return formatMontant(v);
};

const MOIS_COURT = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

// ─── Composant principal ─────────────────────────────────────────────────────
export default function OngletCompteResultat({ comptabilite, selectedYear, compteResultat, saveCompteResultat }) {
  const historique = useMemo(() => {
    const stored = compteResultat?.historique || {};
    const allKeys = new Set([...Object.keys(HIST_DEFAULT), ...Object.keys(stored)]);
    const merged = {};
    for (const k of allKeys) merged[k] = { ...(HIST_DEFAULT[k] || {}), ...(stored[k] || {}) };
    return merged;
  }, [compteResultat]);

  const [detailModal, setDetailModal] = useState(null);
  const [editCell, setEditCell]       = useState(null);
  const [editVal, setEditVal]         = useState('');

  // ─── Années auto : 2025 jusqu'à selectedYear ──────────────────────────────
  const autoYears = useMemo(() => {
    const years = [];
    for (let y = 2026; y <= Math.max(selectedYear, 2026); y++) years.push(y);
    return years;
  }, [selectedYear]);

  const allYears = useMemo(() => [...HIST_YEARS, ...autoYears], [autoYears]);

  // ─── Navigation colonnes sur mobile ──────────────────────────────────────
  const [mobileStart, setMobileStart] = useState(null);
  useEffect(() => {
    const COLS = 3;
    const update = () => {
      if (window.innerWidth < 900) {
        setMobileStart(s => s === null ? Math.max(0, allYears.length - COLS) : Math.min(s, allYears.length - COLS));
      } else {
        setMobileStart(null);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [allYears.length]);
  const MOBILE_COLS = 3;
  const visibleYears = mobileStart !== null ? allYears.slice(mobileStart, mobileStart + MOBILE_COLS) : allYears;
  const stickyTd = { position:'sticky', left:0, zIndex:1, background:'#fff' };
  const stickyTh = { position:'sticky', left:0, zIndex:2, background:'#f9fafb' };

  // ─── Données auto calculées pour chaque année ≥ 2025 ─────────────────────
  const autoData = useMemo(() => {
    const result = {};
    // Toujours calculer 2025 (fallback pour CellHist) + années auto (2026+)
    const yearsToCompute = [...new Set([2025, ...autoYears])];
    for (const yr of yearsToCompute) {
      const rec = {}, dep = {};
      RECETTES_LIGNES.forEach(l => { rec[l.id] = computeLigne(l, comptabilite, yr); });
      DEPENSES_LIGNES.forEach(l => { dep[l.id] = computeLigne(l, comptabilite, yr); });
      result[yr] = { rec, dep };
    }
    return result;
  }, [comptabilite, autoYears]);

  // ─── Totaux ──────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const result = {};
    for (const y of HIST_YEARS) {
      const ad = autoData[y]; // fallback auto pour années avec données comptables (ex: 2025)
      const totRec = RECETTES_LIGNES.reduce((s, l) => {
        const manual = historique[l.id]?.[y];
        if (manual !== undefined) return s + manual;
        return s + (ad?.rec[l.id]?.total || 0);
      }, 0);
      const totDep = DEPENSES_LIGNES.reduce((s, l) => {
        const manual = historique[l.id]?.[y];
        if (manual !== undefined) return s + manual;
        return s + (ad?.dep[l.id]?.total || 0);
      }, 0);
      result[y] = { totRec: Math.round(totRec*100)/100, totDep: Math.round(totDep*100)/100, res: Math.round((totRec-totDep)*100)/100 };
    }
    for (const yr of autoYears) {
      const d = autoData[yr]; if (!d) continue;
      // Manuel prime sur auto si explicitement défini (même à 0)
      const totRec = RECETTES_LIGNES.reduce((s, l) => {
        const m = historique[l.id]?.[yr];
        return s + (m !== undefined ? m : d.rec[l.id].total);
      }, 0);
      const totDep = DEPENSES_LIGNES.reduce((s, l) => {
        const m = historique[l.id]?.[yr];
        return s + (m !== undefined ? m : d.dep[l.id].total);
      }, 0);
      result[yr] = { totRec: Math.round(totRec*100)/100, totDep: Math.round(totDep*100)/100, res: Math.round((totRec-totDep)*100)/100 };
    }
    return result;
  }, [historique, autoData, autoYears]);

  // ─── Edition (historique ou override manuel année auto) ─────────────────
  const startEdit = (ligneId, year, autoVal = null) => {
    setEditCell({ ligneId, year });
    const manual = historique[ligneId]?.[year];
    // Pré-remplit avec la valeur manuelle existante, sinon la valeur auto (pratique pour ajuster)
    setEditVal(String(manual !== undefined ? manual : (autoVal ?? 0)).replace('.', ','));
  };
  const commitEdit = useCallback(() => {
    if (!editCell) return;
    const val = parseFloat(editVal.replace(',', '.')) || 0;
    const newHist = {
      ...historique,
      [editCell.ligneId]: { ...(historique[editCell.ligneId] || {}), [editCell.year]: val, _updatedAt: new Date().toISOString() },
    };
    saveCompteResultat({ historique: newHist });
    setEditCell(null);
  }, [editCell, editVal, historique, saveCompteResultat]);

  const resetOverride = useCallback((ligneId, year) => {
    const newHist = { ...historique };
    if (newHist[ligneId]) {
      const { [year]: _removed, ...rest } = newHist[ligneId];
      newHist[ligneId] = { ...rest, _updatedAt: new Date().toISOString() };
    }
    saveCompteResultat({ historique: newHist });
  }, [historique, saveCompteResultat]);

  // ─── Export PDF (Étape 4 ajoutera la modale de sélection) ───────────────
  const [pdfModal, setPdfModal] = useState(false);
  const [pdfYears, setPdfYears] = useState(null);

  const launchExportPDF = (cols) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = 297, H = 210, mL = 12, mR = 12, cW = W - mL - mR;
    let y = 0;

    const titleYears = cols.length === 1
      ? `Exercice ${cols[0]}`
      : cols[0] + 1 === cols[cols.length - 1] || cols.every((v,i,a) => i === 0 || v === a[i-1]+1)
        ? `${cols[0]} à ${cols[cols.length-1]}`
        : `Comparaison ${cols.join(' / ')}`;

    doc.setFillColor(204, 0, 0);
    doc.rect(0, 0, W, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('SDASSP FO RÉUNION', mL, 9);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`COMPTE DE RÉSULTAT — ${titleYears}`, mL, 16);
    doc.text(`Édité le ${new Date().toLocaleDateString('fr-FR')}`, W - mR, 16, { align: 'right' });
    doc.setTextColor(0);
    y = 28;

    const colW = Math.min(28, Math.floor((cW * 0.6) / cols.length));
    const labelW = cW - cols.length * colW;

    doc.setFillColor(240, 240, 240);
    doc.rect(mL, y - 5, cW, 7, 'F');
    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    cols.forEach((yr, i) => {
      const label = autoYears.includes(yr) ? `${yr}*` : String(yr);
      doc.text(label, mL + labelW + i * colW + colW / 2, y, { align: 'center' });
    });
    y += 5;

    const getVal = (l, yr, section) => {
      if (autoYears.includes(yr)) {
        const d = autoData[yr];
        const v = d ? (section === 'rec' ? d.rec[l.id].total : d.dep[l.id].total) : 0;
        return v > 0 ? v : (historique[l.id]?.[yr] || 0);
      }
      // HIST_YEARS avec données auto disponibles (ex: 2025) : manual prime, sinon auto
      const manual = historique[l.id]?.[yr];
      if (manual !== undefined) return manual;
      const ad = autoData[yr];
      if (ad) return section === 'rec' ? (ad.rec[l.id]?.total || 0) : (ad.dep[l.id]?.total || 0);
      return 0;
    };

    const drawLine = (label, values, isSection = false, isBold = false, color = [0,0,0]) => {
      if (y > H - 15) { doc.addPage(); y = 15; }
      if (isSection) {
        doc.setFillColor(204, 0, 0); doc.rect(mL, y - 4, cW, 7, 'F');
        doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(9);
        doc.text(label, mL + 2, y); doc.setTextColor(0); y += 6; return;
      }
      doc.setFont('helvetica', isBold ? 'bold' : 'normal'); doc.setFontSize(8);
      if (isBold) { doc.setFillColor(245,245,245); doc.rect(mL, y-4, cW, 6, 'F'); }
      doc.setTextColor(...color);
      doc.text(label, mL + 2, y, { maxWidth: labelW - 4 });
      values.forEach((v, i) => {
        doc.text(v === 0 ? '—' : v.toFixed(2).replace('.',','), mL + labelW + i*colW + colW/2, y, { align:'center' });
      });
      doc.setTextColor(0); y += 5.5;
    };

    drawLine('RECETTES', [], true);
    RECETTES_LIGNES.forEach(l => drawLine(l.libelle, cols.map(yr => getVal(l, yr, 'rec'))));
    drawLine('TOTAL RECETTES', cols.map(yr => totals[yr]?.totRec || 0), false, true, [22,101,52]);
    y += 2;
    drawLine('DÉPENSES', [], true);
    DEPENSES_LIGNES.forEach(l => drawLine(l.libelle, cols.map(yr => getVal(l, yr, 'dep'))));
    drawLine('TOTAL DÉPENSES', cols.map(yr => totals[yr]?.totDep || 0), false, true, [153,27,27]);
    y += 2;

    const resVals = cols.map(yr => totals[yr]?.res || 0);
    if (y > H - 18) { doc.addPage(); y = 15; }
    doc.setFillColor(30,30,30); doc.rect(mL, y-4, cW, 10, 'F');
    doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(10);
    doc.text("RÉSULTAT DE L'EXERCICE", mL+2, y+2);
    resVals.forEach((v, i) => {
      doc.setTextColor(v>=0?134:255, v>=0?239:100, v>=0?172:100);
      doc.text((v>=0?'+':'')+v.toFixed(2).replace('.',',')+' €', mL+labelW+i*colW+colW/2, y+2, { align:'center' });
    });
    doc.setTextColor(0);
    doc.save(`Compte-Resultat-SDASSP-${titleYears.replace(/ /g,'_')}.pdf`);
  };

  const exportPDF = () => setPdfModal(true);

  // ─── Rendu tableau ───────────────────────────────────────────────────────
  const CellHist = ({ ligneId, year, section }) => {
    const isEditing = editCell?.ligneId === ligneId && editCell?.year === year;
    const manualVal = historique[ligneId]?.[year];
    // Fallback auto pour années dans HIST_YEARS qui ont aussi des données comptables (2025)
    const ad = autoData[year];
    const autoFb = ad && section
      ? (section === 'rec' ? ad.rec[ligneId]?.total : ad.dep[ligneId]?.total)
      : undefined;
    const val = manualVal !== undefined ? manualVal : (autoFb || 0);
    const isAutoFb = manualVal === undefined && autoFb > 0;

    if (isEditing) {
      return (
        <td style={{ textAlign: 'right', padding: '2px 4px' }}>
          <input
            autoFocus
            type="text"
            inputMode="decimal"
            value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditCell(null); }}
            style={{ width: 80, textAlign: 'right', fontSize: 12, border: '1px solid #CC0000', borderRadius: 4, padding: '1px 4px' }}
          />
        </td>
      );
    }
    return (
      <td
        style={{
          textAlign: 'right', cursor: 'pointer', fontSize: 12, padding: '4px 8px',
          color: val ? (isAutoFb ? '#166534' : '#1f2937') : '#d1d5db',
          background: isAutoFb ? '#f0fdf4' : undefined,
        }}
        title={isAutoFb ? 'Calculé depuis les relevés · cliquez pour saisir manuellement' : 'Cliquez pour modifier'}
        onClick={() => startEdit(ligneId, year, isAutoFb ? autoFb : undefined)}>
        {val ? formatMontant(val) : '—'}
      </td>
    );
  };

  const CellAuto = ({ ligne, year, section }) => {
    const d = autoData[year];
    const auto = d ? (section==='rec' ? d.rec[ligne.id] : d.dep[ligne.id]) : { total:0, ops:[] };
    const manualOverride = historique[ligne.id]?.[year]; // undefined = pas de saisie manuelle
    const hasManual = manualOverride !== undefined;
    const isEditing = editCell?.ligneId === ligne.id && editCell?.year === year;

    if (isEditing) return (
      <td style={{ textAlign:'right', padding:'2px 4px', background:'#fffbeb' }}>
        <input autoFocus type="text" inputMode="decimal" value={editVal}
          onChange={e => setEditVal(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => { if(e.key==='Enter') commitEdit(); if(e.key==='Escape') setEditCell(null); }}
          style={{ width:80, textAlign:'right', fontSize:12, border:'1px solid #d97706', borderRadius:4, padding:'1px 4px' }} />
      </td>
    );

    // ── Override manuel actif : prioritaire sur l'auto ──
    if (hasManual) return (
      <td style={{ textAlign:'right', fontSize:12, padding:'4px 8px', background:'#fffbeb', position:'relative' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:4 }}>
          <span style={{ fontSize:9, color:'#92400e', background:'#fde68a', padding:'1px 4px', borderRadius:3, fontWeight:600 }}>Manuel</span>
          <span style={{ fontWeight:600, color:'#92400e', cursor:'pointer' }}
            title="Cliquez pour modifier" onClick={() => startEdit(ligne.id, year, auto.total)}>
            {manualOverride ? formatMontant(manualOverride) : '—'}
          </span>
          <button onClick={e => { e.stopPropagation(); resetOverride(ligne.id, year); }}
            title="Revenir à la valeur auto"
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:10, color:'#b45309', padding:'0 2px', lineHeight:1 }}>✖</button>
        </div>
        {auto.ops.length > 0 && (
          <div style={{ fontSize:9, color:'#9ca3af', cursor:'pointer' }}
            onClick={() => setDetailModal({ ligne, section, ops: auto.ops, total: auto.total, year })}
            title="Voir les opérations auto (hors calcul)">
            auto: {formatMontant(auto.total)}
          </div>
        )}
      </td>
    );

    // ── Valeur auto disponible (pas d'override) ──
    if (auto.ops.length > 0) return (
      <td style={{ textAlign:'right', fontSize:12, padding:'4px 8px', color:'#166534', fontWeight:600,
        cursor:'pointer', textDecoration:'underline dotted', background:'#f0fdf4' }}
        title={`${auto.ops.length} opération(s) · clic = détail · double-clic = saisie manuelle`}
        onClick={() => setDetailModal({ ligne, section, ops: auto.ops, total: auto.total, year })}
        onDoubleClick={e => { e.stopPropagation(); startEdit(ligne.id, year, auto.total); }}>
        {formatMontant(auto.total)}
      </td>
    );

    // ── Aucune donnée ──
    return (
      <td style={{ textAlign:'right', cursor:'pointer', fontSize:12, padding:'4px 8px', color:'#d1d5db', background:'#f8faff' }}
        title="Aucune donnée · cliquez pour saisir manuellement"
        onClick={() => startEdit(ligne.id, year, 0)}>
        —
      </td>
    );
  };

  const renderSection = (lignes, section, label, color) => (
    <>
      <tr>
        <td colSpan={visibleYears.length + 1} style={{ background: color, color: '#fff', fontWeight: 700, fontSize: 13, padding: '8px 12px', letterSpacing: 1 }}>
          {label}
        </td>
      </tr>
      {lignes.map(l => (
        <tr key={l.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
          <td style={{ padding: '6px 12px', fontSize: 13, color: '#374151', ...stickyTd }}>{l.libelle}</td>
          {visibleYears.map(y => HIST_YEARS.includes(y)
            ? <CellHist key={y} ligneId={l.id} year={y} section={section} />
            : <CellAuto key={y} ligne={l} year={y} section={section} />)}
        </tr>
      ))}
    </>
  );

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>📋 Compte de Résultat Simplifié</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            Colonnes 2021–2025 : données historiques (cliquez pour modifier) · 2025 affiche les valeurs issues des relevés, éditables en un clic{autoYears.length > 0 ? ` · Colonnes ${autoYears.join(', ')} : auto depuis relevés` : ''}
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={exportPDF} style={{ display:'flex', alignItems:'center', gap:6 }}>
          📄 Exporter PDF
        </button>
      </div>

      {/* Navigation colonnes mobile */}
      {mobileStart !== null && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#f0f9ff', borderRadius:8, padding:'8px 12px', marginBottom:8, border:'1px solid #bae6fd' }}>
          <button
            style={{ background: mobileStart === 0 ? '#e5e7eb' : '#1d4ed8', color: mobileStart === 0 ? '#9ca3af' : '#fff', border:'none', borderRadius:6, padding:'6px 14px', fontSize:13, fontWeight:700, cursor: mobileStart === 0 ? 'not-allowed' : 'pointer' }}
            disabled={mobileStart === 0}
            onClick={() => setMobileStart(s => Math.max(0, s - 1))}>◀ Préc.</button>
          <span style={{ fontSize:13, fontWeight:700, color:'#1d4ed8' }}>{visibleYears.join(' · ')}</span>
          <button
            style={{ background: mobileStart >= allYears.length - MOBILE_COLS ? '#e5e7eb' : '#1d4ed8', color: mobileStart >= allYears.length - MOBILE_COLS ? '#9ca3af' : '#fff', border:'none', borderRadius:6, padding:'6px 14px', fontSize:13, fontWeight:700, cursor: mobileStart >= allYears.length - MOBILE_COLS ? 'not-allowed' : 'pointer' }}
            disabled={mobileStart >= allYears.length - MOBILE_COLS}
            onClick={() => setMobileStart(s => Math.min(allYears.length - MOBILE_COLS, s + 1))}>Suiv. ▶</button>
        </div>
      )}

      {/* Tableau */}
      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', fontWeight: 700, width: '34%', ...stickyTh }}>Libellé</th>
              {visibleYears.map(y => HIST_YEARS.includes(y) ? (
                <th key={y} style={{ textAlign: 'right', padding: '10px 8px', fontSize: 12, color: '#6b7280', fontWeight: 700 }}>
                  {y}<div style={{ fontSize: 9, fontWeight: 400, color: '#9ca3af' }}>✏️ modifiable</div>
                </th>
              ) : (
                <th key={y} style={{ textAlign: 'right', padding: '10px 8px', fontSize: 12, color: '#1d4ed8', fontWeight: 700, background: y === selectedYear ? '#eff6ff' : '#f0f9ff' }}>
                  {y}<div style={{ fontSize: 9, fontWeight: 400, color: '#93c5fd' }}>🔄 auto{y < selectedYear ? '' : ' (actuel)'}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderSection(RECETTES_LIGNES, 'rec', '▲ RECETTES', '#166534')}
            {/* Sous-total RECETTES */}
            <tr style={{ background: '#f0fdf4', borderTop: '2px solid #bbf7d0' }}>
              <td style={{ padding: '8px 12px', fontWeight: 700, fontSize: 13, color: '#166534', ...stickyTd, background:'#f0fdf4' }}>TOTAL RECETTES</td>
              {visibleYears.map(y => (
                <td key={y} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#166534', padding: '8px 8px', background: !HIST_YEARS.includes(y) ? (y === selectedYear ? '#eff6ff' : '#f0f9ff') : undefined }}>
                  {totals[y]?.totRec ? formatMontant(totals[y].totRec) : '—'}
                </td>
              ))}
            </tr>

            <tr><td colSpan={visibleYears.length + 1} style={{ height: 12 }} /></tr>

            {renderSection(DEPENSES_LIGNES, 'dep', '▼ DÉPENSES', '#991b1b')}
            {/* Sous-total DÉPENSES */}
            <tr style={{ background: '#fff1f2', borderTop: '2px solid #fecdd3' }}>
              <td style={{ padding: '8px 12px', fontWeight: 700, fontSize: 13, color: '#991b1b', ...stickyTd, background:'#fff1f2' }}>TOTAL DÉPENSES</td>
              {visibleYears.map(y => (
                <td key={y} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#991b1b', padding: '8px 8px', background: !HIST_YEARS.includes(y) ? (y === selectedYear ? '#eff6ff' : '#f0f9ff') : undefined }}>
                  {totals[y]?.totDep ? formatMontant(totals[y].totDep) : '—'}
                </td>
              ))}
            </tr>

            <tr><td colSpan={visibleYears.length + 1} style={{ height: 4 }} /></tr>

            {/* RÉSULTAT */}
            <tr style={{ borderTop: '3px solid #374151' }}>
              <td style={{ padding: '12px 12px', fontWeight: 800, fontSize: 15, color: '#111', ...stickyTd }}>RÉSULTAT</td>
              {visibleYears.map(y => {
                const r = totals[y]?.res ?? 0;
                const isAuto = !HIST_YEARS.includes(y);
                const color = r >= 0 ? '#166534' : '#991b1b';
                return (
                  <td key={y} style={{ textAlign:'right', fontWeight:800, fontSize:15, padding:'12px 8px', color, background: isAuto ? (y===selectedYear ? '#eff6ff' : '#f0f9ff') : undefined }}>
                    {r !== 0
                      ? isAuto
                        ? <><span>{(r>0?'+':'')+formatMontant(r)}</span><br/><span style={{fontSize:10,fontWeight:400}}>{r>=0?'✅ Excédent':'❌ Déficit'}</span></>
                        : (r > 0 ? '+' : '') + formatMontant(r)
                      : '—'}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Légende */}
      <div style={{ display:'flex', gap:16, marginTop:12, fontSize:11, color:'#6b7280', flexWrap:'wrap' }}>
        <span>✏️ 2021–2024 : cliquez pour modifier.</span>
        <span>🔄 {autoYears.join(', ')} : auto depuis relevés · double-clic pour override manuel.</span>
        <span>💡 Pour alimenter une ligne, catégorisez vos opérations dans l'onglet Import.</span>
      </div>

      {/* Modal détail opérations */}
      {/* Modale PDF */}
      {pdfModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1001, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={() => setPdfModal(false)}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:380, width:'100%' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>� Exporter le PDF</div>
            <div style={{ fontSize:12, color:'#6b7280', marginBottom:16 }}>Sélectionnez les années à inclure dans le tableau :</div>
            {allYears.map(y => (
              <label key={y} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 4px', cursor:'pointer', fontSize:13 }}>
                <input type="checkbox" checked={(pdfYears ?? allYears).includes(y)}
                  onChange={e => {
                    const base = pdfYears ?? allYears;
                    setPdfYears(e.target.checked ? [...base, y].sort((a,b)=>a-b) : base.filter(x=>x!==y));
                  }} />
                <span>{y}</span>
                {autoYears.includes(y) && <span style={{fontSize:10,color:'#1d4ed8'}}>� auto</span>}
              </label>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:16, justifyContent:'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setPdfModal(false); setPdfYears(null); }}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={() => {
                const cols = (pdfYears ?? allYears).filter(y => y);
                if (cols.length === 0) return;
                launchExportPDF(cols);
                setPdfModal(false); setPdfYears(null);
              }}>Exporter</button>
            </div>
          </div>
        </div>
      )}

      {detailModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={() => setDetailModal(null)}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:640, width:'100%', maxHeight:'80vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:15 }}>{detailModal.ligne.libelle}</div>
                <div style={{ fontSize:12, color:'#6b7280' }}>Exercice {detailModal.year ?? selectedYear} · {detailModal.ops.length} opération(s) · Total : {formatMontant(detailModal.total)}</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setDetailModal(null)}>✕</button>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
                  <th style={{ textAlign:'left', padding:'6px 8px' }}>Mois</th>
                  <th style={{ textAlign:'left', padding:'6px 8px' }}>Date</th>
                  <th style={{ textAlign:'left', padding:'6px 8px' }}>Libellé</th>
                  <th style={{ textAlign:'right', padding:'6px 8px' }}>{detailModal.section === 'rec' ? 'Crédit' : 'Débit'}</th>
                </tr>
              </thead>
              <tbody>
                {detailModal.ops.map((op, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ padding:'5px 8px', color:'#6b7280' }}>{MOIS_COURT[(op.mois || 1) - 1]}</td>
                    <td style={{ padding:'5px 8px', color:'#6b7280', whiteSpace:'nowrap' }}>{op.dateAffichage || op.date?.slice(5)?.replace('-','/')}</td>
                    <td style={{ padding:'5px 8px', fontFamily:'monospace', fontSize:11 }}>{op.libelleBrut || op.libelle || '—'}</td>
                    <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:600, color: detailModal.section === 'rec' ? '#166534' : '#991b1b' }}>
                      {formatMontant(detailModal.section === 'rec' ? (op.credit||0) : (op.debit||0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
